import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { MarketingShell, Seo, breadcrumbLd } from '../seo/kit';

const NAVY = '#13294B';
const TEXT = '#1D2939';
const SUBTLE = '#5B6678';
const BORDER = '#D8DEE8';
const LIGHT = '#F2F4F7';
const HERO_BG = '#EEF2F8';
const BAND_BODY = '#DCE3EE';
const BLUE = '#1E4ED8';

const Container = ({ children, className = '' }) => (
  <div className={`max-w-7xl mx-auto px-5 md:px-8 ${className}`}>{children}</div>
);

const BODY = 'text-[17px] md:text-[18px] leading-relaxed';
const h1Style = { fontWeight: 800, letterSpacing: '-0.02em' };
const hStyle = { fontWeight: 700, letterSpacing: '-0.01em' };

const bullets = {
  builder: [
    'Enter a draw by budget line and see remaining balance as you go',
    'Attach invoices, lien waivers, COIs and photos to the line they support',
    'Catch over-budget lines before your lender does',
    'Track every draw, project and dollar in one place',
  ],
  lender: [
    'Every request checked against the approved budget by category',
    'Over-advance and missing-document flags before review',
    'One approval screen with the full draw history',
    'Pricing built around your active loan portfolio',
  ],
};

const steps = [
  { n: '1', t: 'Request', d: 'The builder enters amounts against the budget lines being billed.' },
  { n: '2', t: 'Attach', d: 'Invoices, lien waivers and certificates go on the line they support.' },
  { n: '3', t: 'Check', d: 'DrawReady flags anything over budget or missing before submission.' },
  { n: '4', t: 'Approve', d: 'The lender reviews one complete package and funds the draw.' },
];

const faqs = [
  { q: 'What is the Draw Score?', a: 'A 0–100 snapshot of how ready a draw is. It checks invoices, COIs, lien waivers, W-9s and inspections, plus anything else that could delay funding, and lists exactly what to collect.' },
  { q: 'Is this about drawings or blueprints?', a: 'No. A construction draw is a release of loan funds as work gets completed — not architectural drawings. DrawReady helps you request, document and submit those funding draws.' },
  { q: 'Does my bank need to use DrawReady?', a: 'No. You build and verify the package in DrawReady, then send it to your lender however they already prefer. Your bank does not need an account.' },
  { q: 'What documents can I attach?', a: 'Invoices, lien waivers, certificates of insurance, inspections and photos — each attached to the specific budget line it supports.' },
  { q: 'How is my data protected?', a: 'Your project data is encrypted in transit and at rest, and only the people you invite to a project can see it.' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <MarketingShell>
      <Seo
        title="DrawReady | Submit a bank-ready draw in 30 seconds"
        description="DrawReady shows exactly what your lender still needs before you submit. Fix what's missing and send a draw that doesn't come back."
        path="/"
        jsonLd={breadcrumbLd([{ label: 'Home', href: '/' }])}
      />

      {/* 1. Hero — light blue-gray, Draw Score widget on the right */}
      <section style={{ background: HERO_BG }}>
        <Container>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center min-h-[70vh] py-16 md:py-[120px]">
            <div>
              <h1 className="text-[40px] leading-[1.05] sm:text-6xl lg:text-[80px]" style={h1Style}>
                Submit a bank-ready draw in 30 seconds.
              </h1>
              <p className={`${BODY} mt-6 max-w-xl`} style={{ color: SUBTLE }}>
                Your Draw Score shows exactly what your lender still needs before you submit. Fix what's missing, hit 100, and send a draw that doesn't come back.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <button onClick={() => navigate('/signup')} className="btn-primary" data-testid="hero-start-trial">Start your 30-day free trial</button>
                <button onClick={() => navigate('/partners')} className="btn-secondary" data-testid="hero-lender">I'm a lender</button>
              </div>
            </div>
            <div>
              <img
                src="/images/drawready-draw-score.jpg"
                alt="The DrawReady Draw Score on a live project"
                className="w-full"
                style={{ borderRadius: '12px', border: `1px solid ${BORDER}`, boxShadow: '0 10px 30px rgba(19,41,75,0.12)' }}
                loading="eager"
              />
              <p className="text-[14px] mt-3" style={{ color: SUBTLE }}>The Draw Score on a live project.</p>
            </div>
          </div>
        </Container>
      </section>

      {/* 2. Problem headline — navy band */}
      <section style={{ background: NAVY }}>
        <Container className="py-16 md:py-20">
          <h2 className="text-3xl sm:text-4xl max-w-3xl" style={{ ...hStyle, color: '#fff' }}>One missing lien waiver can hold up a $58,000 draw.</h2>
          <p className={`${BODY} mt-4 max-w-2xl`} style={{ color: BAND_BODY }}>
            The Draw Score counts every document and inspection that could delay funding, so you find the gap before your lender does.
          </p>
        </Container>
      </section>

      {/* 3. Builders / Lenders split — white */}
      <section style={{ background: '#fff' }}>
        <Container className="py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-12">
            <div className="md:pr-8">
              <h3 className="text-2xl mb-2" style={hStyle}>For builders and developers</h3>
              <p className={`${BODY} mb-6`} style={{ color: SUBTLE }}>Stop finding out what's missing after the bank sends it back.</p>
              <ul className="space-y-3">
                {bullets.builder.map((b) => (
                  <li key={b} className={`${BODY} flex items-start gap-3`} style={{ color: TEXT }}><span className="mt-2.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: NAVY }} />{b}</li>
                ))}
              </ul>
              <button onClick={() => navigate('/construction-draw-software-for-developers')} className="mt-7 font-semibold text-[17px]" style={{ color: BLUE }}>How developers use DrawReady</button>
            </div>
            <div className="md:pl-16 md:border-l" style={{ borderColor: BORDER }}>
              <h3 className="text-2xl mb-2" style={hStyle}>For banks and lenders</h3>
              <p className={`${BODY} mb-6`} style={{ color: SUBTLE }}>Get complete, consistent draw packages across your construction portfolio.</p>
              <ul className="space-y-3">
                {bullets.lender.map((b) => (
                  <li key={b} className={`${BODY} flex items-start gap-3`} style={{ color: TEXT }}><span className="mt-2.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: NAVY }} />{b}</li>
                ))}
              </ul>
              <button onClick={() => navigate('/partners')} className="mt-7 font-semibold text-[17px]" style={{ color: BLUE }}>Talk with us about your portfolio</button>
            </div>
          </div>
        </Container>
      </section>

      {/* 4. Product showcase — light gray */}
      <section style={{ background: LIGHT }}>
        <Container className="py-16 md:py-24">
          <h2 className="text-3xl sm:text-4xl max-w-3xl" style={hStyle}>Budget against actual by phase, with each line's readiness status.</h2>
          <p className={`${BODY} mt-4 max-w-2xl`} style={{ color: SUBTLE }}>Pay, attach docs, request a draw, or schedule an inspection right from the row.</p>
          <img src="/images/budget-progress.webp" alt="DrawReady Budget & Progress — phases, budget vs actual and readiness per line" className="w-full border mt-8" style={{ borderColor: BORDER }} loading="lazy" />
          <p className="text-sm mt-3" style={{ color: SUBTLE }}>Budget &amp; Progress on a single-family build.</p>
        </Container>
      </section>

      {/* 5. Process flow — white */}
      <section style={{ background: '#fff' }}>
        <Container className="py-16 md:py-24">
          <h2 className="text-3xl sm:text-4xl mb-10" style={hStyle}>How a draw moves through DrawReady</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {steps.map((s) => (
              <div key={s.n}>
                <p className="text-sm font-semibold mb-3" style={{ color: BLUE }}>{s.n}</p>
                <h3 className="text-xl mb-2" style={hStyle}>{s.t}</h3>
                <p className={BODY} style={{ color: SUBTLE }}>{s.d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* 6. Brand story — navy, foundation photo bleeding off the left edge */}
      <section className="relative overflow-hidden" style={{ background: NAVY }}>
        <img
          src="/images/founder-foundation.jpeg"
          alt="Poured concrete foundation on a residential build site"
          className="hidden lg:block absolute left-0 top-0 h-full w-1/2 object-cover"
          loading="lazy"
        />
        <Container className="relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="lg:hidden order-first">
              <img src="/images/founder-foundation.jpeg" alt="Poured concrete foundation on a residential build site" className="w-full h-64 object-cover" loading="lazy" />
            </div>
            <div className="hidden lg:block" aria-hidden="true" />
            <div className="py-14 md:py-24 lg:pl-8">
              <h2 className="text-2xl sm:text-3xl mb-4" style={{ ...hStyle, color: '#fff' }}>Built by people who run construction draws, not just write software about them.</h2>
              <p className={BODY} style={{ color: BAND_BODY }}>
                DrawReady comes out of real development and contracting work — managing draws from both sides of the table, as the builder submitting and the party reviewing. That's why we build for both sides of the draw.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* 7. Finished home photo — full-bleed, edge to edge */}
      <section>
        <img src="/images/home-finished.webp" alt="Completed single-family home" className="block w-full h-[320px] md:h-[520px] object-cover" loading="lazy" />
      </section>

      {/* 8. FAQ — white */}
      <section style={{ background: '#fff' }}>
        <Container className="py-16 md:py-24">
          <div className="max-w-3xl">
            <h2 className="text-3xl sm:text-4xl mb-8" style={hStyle}>Questions</h2>
            <div className="border-t border-b" style={{ borderColor: BORDER }}>
              {faqs.map((f, i) => (
                <div key={f.q} className="border-b last:border-b-0" style={{ borderColor: BORDER }}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between text-left py-5" data-testid={`faq-toggle-${i}`}>
                    <span className="text-lg font-semibold pr-6" style={{ color: NAVY }}>{f.q}</span>
                    <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} style={{ color: SUBTLE }} />
                  </button>
                  {openFaq === i && <p className={`${BODY} pb-5 -mt-1 pr-8`} style={{ color: SUBTLE }}>{f.a}</p>}
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* 9. Final CTA — blue block */}
      <section style={{ background: BLUE }}>
        <Container className="py-16 md:py-20">
          <h2 className="text-3xl sm:text-4xl" style={{ ...hStyle, color: '#fff' }}>Have a draw coming up? Build it in DrawReady first.</h2>
          <div className="mt-7">
            <button
              onClick={() => navigate('/signup')}
              className="font-semibold text-[16px] px-6 py-3"
              style={{ background: '#fff', color: BLUE }}
              data-testid="footer-cta-start-trial"
            >
              Start your 30-day free trial
            </button>
          </div>
        </Container>
      </section>
    </MarketingShell>
  );
};

export default LandingPage;
